import React, { useState, useEffect } from 'react';
import { CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/card';
import { Button } from '../../../../components/ui/button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');

  const [checking, setChecking] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [invoiceId, setInvoiceId] = useState(null);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setChecking(false);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId) => {
    try {
      // Poll payment status from backend
      let attempts = 0;
      const maxAttempts = 5;
      const pollInterval = 2000;

      const poll = async () => {
        if (attempts >= maxAttempts) {
          setPaymentStatus('timeout');
          setChecking(false);
          return;
        }

        try {
          const response = await fetch(
            `${API_URL}/api/finance/stripe/status/${sessionId}`,
            { headers: { 'Authorization': `Bearer ${token}` } }
          );

          if (!response.ok) throw new Error('Failed to check payment status');

          const data = await response.json();

          if (data.payment_status === 'paid') {
            setPaymentStatus('success');
            setInvoiceId(data.invoice_id);
            setChecking(false);
            toast({
              title: 'Payment Successful',
              description: 'Your payment has been processed successfully'
            });
          } else if (data.status === 'expired') {
            setPaymentStatus('expired');
            setChecking(false);
          } else {
            // Still pending, continue polling
            attempts++;
            setTimeout(poll, pollInterval);
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
          attempts++;
          setTimeout(poll, pollInterval);
        }
      };

      poll();
    } catch (error) {
      console.error('Error in payment status check:', error);
      setPaymentStatus('error');
      setChecking(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Card className="max-w-md w-full">
        <CardContent className="pt-6">
          {checking ? (
            <div className="text-center py-8">
              <Loader2 className="h-16 w-16 mx-auto mb-4 text-[#FF6B35] animate-spin" />
              <h3 className="text-xl font-bold text-[#2B2D42] mb-2">
                Processing Payment...
              </h3>
              <p className="text-sm text-[#6C757D]">
                Please wait while we confirm your payment
              </p>
            </div>
          ) : paymentStatus === 'success' ? (
            <div className="text-center py-8">
              <div className="mx-auto mb-6 h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-[#2B2D42] mb-2">
                Payment Successful!
              </h3>
              <p className="text-sm text-[#6C757D] mb-6">
                Your payment has been processed and the invoice has been updated.
              </p>
              <div className="flex flex-col space-y-3">
                {invoiceId && (
                  <Button
                    onClick={() => navigate(`/admin/procurement/invoices/${invoiceId}`)}
                    className="bg-[#FF6B35] hover:bg-[#E5562D]"
                  >
                    View Invoice
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/procurement/invoices')}
                >
                  Back to Invoices
                </Button>
              </div>
            </div>
          ) : paymentStatus === 'timeout' ? (
            <div className="text-center py-8">
              <div className="mx-auto mb-6 h-16 w-16 rounded-full bg-yellow-100 flex items-center justify-center">
                <Loader2 className="h-10 w-10 text-yellow-600" />
              </div>
              <h3 className="text-2xl font-bold text-[#2B2D42] mb-2">
                Payment Processing
              </h3>
              <p className="text-sm text-[#6C757D] mb-6">
                Your payment is being processed. Please check your email for confirmation.
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/procurement/invoices')}
              >
                Back to Invoices
              </Button>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="mx-auto mb-6 h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
                <CheckCircle className="h-10 w-10 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-[#2B2D42] mb-2">
                Payment Error
              </h3>
              <p className="text-sm text-[#6C757D] mb-6">
                There was an issue processing your payment. Please try again.
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/admin/procurement/invoices')}
              >
                Back to Invoices
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
